import { useCallback, useEffect, useState } from 'react';
import {
    blobToPreviewUrl,
    blobToRawBase64,
    ensureBlobUnderMaxBytes,
    fileToRawBase64,
    getCroppedImageBlob,
    isAllowedCreativeFile,
    isImageFile,
    isTargetAspectRatio,
    isVideoFile,
    loadVideoDimensions,
    MAX_CREATIVE_BYTES,
    MAX_CREATIVE_VIDEO_BYTES,
    MAX_CREATIVE_VIDEO_TRANSCODE_THRESHOLD_BYTES,
    OUTPUT_SIZE,
    prepareImageFileForUpload,
    resizeImageToOutputBlob,
} from '../utils/creativeAspectRatio';

export function useCampaignCreativeUpload(inputIds) {
    const [campaignHorizontalContent, setCampaignHorizontalContent] = useState('');
    const [campaignVerticalContent, setCampaignVerticalContent] = useState('');
    const [creativeEditor, setCreativeEditor] = useState(null);
    const [cropExporting, setCropExporting] = useState(false);
    const [creativeFileNames, setCreativeFileNames] = useState({ horizontal: '', vertical: '' });
    const [creativeProcessing, setCreativeProcessing] = useState(null);

    const revokeCreativeEditorUrls = useCallback((editor) => {
        if (!editor) {
            return;
        }
        if (editor.previewUrl && editor.previewUrl.startsWith('blob:')) {
            URL.revokeObjectURL(editor.previewUrl);
        }
    }, []);

    const clearCreativeInput = useCallback((inputId) => {
        const input = document.querySelector(`#${inputId}`);
        if (input) {
            input.value = '';
        }
    }, []);

    const closeCreativeEditor = useCallback(() => {
        setCreativeEditor((prev) => {
            revokeCreativeEditorUrls(prev);
            return null;
        });
        setCropExporting(false);
    }, [revokeCreativeEditorUrls]);

    const clearSlotContent = useCallback((slot) => {
        if (slot === 'horizontal') {
            setCampaignHorizontalContent('');
        } else {
            setCampaignVerticalContent('');
        }
        setCreativeFileNames((prev) => ({ ...prev, [slot]: '' }));
    }, []);

    const resetCreatives = useCallback(() => {
        setCampaignHorizontalContent('');
        setCampaignVerticalContent('');
        setCreativeFileNames({ horizontal: '', vertical: '' });
        closeCreativeEditor();
    }, [closeCreativeEditor]);

    const commitCreativeBase64 = useCallback((slot, base64, fileName) => {
        if (slot === 'horizontal') {
            setCampaignHorizontalContent(base64);
        } else {
            setCampaignVerticalContent(base64);
        }
        setCreativeFileNames((prev) => ({ ...prev, [slot]: fileName }));
    }, []);

    const openImagePreviewEditor = useCallback(async (slot, file, dataUrl, inputId) => {
        const outputSize = OUTPUT_SIZE[slot];
        let blob = await resizeImageToOutputBlob(dataUrl, outputSize);
        blob = await ensureBlobUnderMaxBytes(blob);
        const previewUrl = blobToPreviewUrl(blob);
        setCreativeEditor({
            slot,
            step: 'preview',
            imageSrc: dataUrl,
            file,
            previewUrl,
            pendingBlob: blob,
            isVideo: false,
            inputId,
        });
    }, []);

    const processCreativeUpload = useCallback(async (e, slot) => {
        const inputId = inputIds[slot];
        const file = e.target.files?.[0];
        if (!file) {
            return;
        }

        if (!isAllowedCreativeFile(file)) {
            alert('Please upload a file with the file type JPG, PNG, MP4, MOV, or WebM');
            clearCreativeInput(inputId);
            setCreativeFileNames((prev) => ({ ...prev, [slot]: '' }));
            return;
        }

        closeCreativeEditor();
        clearSlotContent(slot);
        setCreativeFileNames((prev) => ({ ...prev, [slot]: file.name }));

        const needsResize = isImageFile(file) && file.size > MAX_CREATIVE_BYTES;
        const processingMessage = isVideoFile(file)
            ? 'Processing your video…'
            : needsResize
                ? 'Resizing your image…'
                : 'Preparing your image…';

        setCreativeProcessing({ message: processingMessage });

        try {
            if (isVideoFile(file)) {
                if (file.size > MAX_CREATIVE_VIDEO_BYTES) {
                    alert('This video is too large. Maximum upload size is 80MB. Try a shorter clip or lower resolution.');
                    clearCreativeInput(inputId);
                    setCreativeFileNames((prev) => ({ ...prev, [slot]: '' }));
                    return;
                }
                if (file.size > MAX_CREATIVE_VIDEO_TRANSCODE_THRESHOLD_BYTES) {
                    alert('Large videos are automatically re-encoded to reduce size and fit signage playback.');
                }
                const { width, height } = await loadVideoDimensions(file);
                if (!isTargetAspectRatio(width, height, slot)) {
                    alert(slot === 'horizontal'
                        ? 'Video aspect ratio must be 16:9'
                        : 'Video aspect ratio must be 9:16');
                    clearCreativeInput(inputId);
                    setCreativeFileNames((prev) => ({ ...prev, [slot]: '' }));
                    return;
                }
                const previewUrl = blobToPreviewUrl(file);
                setCreativeEditor({
                    slot,
                    step: 'preview',
                    file,
                    previewUrl,
                    pendingBlob: file,
                    isVideo: true,
                    videoMimeType: file.type || 'video/mp4',
                    inputId,
                });
                return;
            }

            if (!isImageFile(file)) {
                alert('Please upload a file with the file type JPG, PNG, MP4, MOV, or WebM');
                clearCreativeInput(inputId);
                setCreativeFileNames((prev) => ({ ...prev, [slot]: '' }));
                return;
            }

            const { file: imageFile, dataUrl } = await prepareImageFileForUpload(file);
            const image = await new Promise((resolve, reject) => {
                const img = new Image();
                img.onload = () => resolve(img);
                img.onerror = reject;
                img.src = dataUrl;
            });

            if (isTargetAspectRatio(image.width, image.height, slot)) {
                await openImagePreviewEditor(slot, imageFile, dataUrl, inputId);
                return;
            }

            setCreativeEditor({
                slot,
                step: 'crop',
                imageSrc: dataUrl,
                file: imageFile,
                inputId,
            });
        } catch (err) {
            console.error('Creative upload failed:', err);
            alert('Could not process this file. Please try another image or video.');
            clearCreativeInput(inputId);
            setCreativeFileNames((prev) => ({ ...prev, [slot]: '' }));
        } finally {
            setCreativeProcessing(null);
        }
    }, [
        inputIds,
        clearCreativeInput,
        clearSlotContent,
        closeCreativeEditor,
        commitCreativeBase64,
        openImagePreviewEditor,
    ]);

    const handleCreativeEditorCancel = useCallback(() => {
        if (creativeEditor?.inputId) {
            clearCreativeInput(creativeEditor.inputId);
        }
        if (creativeEditor?.slot) {
            setCreativeFileNames((prev) => ({ ...prev, [creativeEditor.slot]: '' }));
        }
        closeCreativeEditor();
    }, [creativeEditor, clearCreativeInput, closeCreativeEditor]);

    const handleCropNext = useCallback(async ({ croppedAreaPixels, rotation }) => {
        if (!creativeEditor || creativeEditor.step !== 'crop') {
            return;
        }
        setCropExporting(true);
        try {
            let blob = await getCroppedImageBlob(
                creativeEditor.imageSrc,
                croppedAreaPixels,
                rotation,
                OUTPUT_SIZE[creativeEditor.slot]
            );
            blob = await ensureBlobUnderMaxBytes(blob);
            const previewUrl = blobToPreviewUrl(blob);
            setCreativeEditor((prev) => {
                revokeCreativeEditorUrls(prev);
                return {
                    slot: prev.slot,
                    step: 'preview',
                    imageSrc: prev.imageSrc,
                    file: prev.file,
                    previewUrl,
                    pendingBlob: blob,
                    isVideo: false,
                    inputId: prev.inputId,
                };
            });
        } catch (err) {
            console.error('Crop export failed:', err);
            alert('Could not crop this image. Please try again.');
        } finally {
            setCropExporting(false);
        }
    }, [creativeEditor, revokeCreativeEditorUrls]);

    const handlePreviewNext = useCallback(async () => {
        if (!creativeEditor?.pendingBlob) {
            return;
        }
        try {
            const base64 = creativeEditor.isVideo
                ? await fileToRawBase64(creativeEditor.file)
                : await blobToRawBase64(creativeEditor.pendingBlob);
            commitCreativeBase64(creativeEditor.slot, base64, creativeEditor.file.name);
            closeCreativeEditor();
        } catch (err) {
            console.error('Preview commit failed:', err);
            alert('Could not save this creative. Please try again.');
        }
    }, [creativeEditor, closeCreativeEditor, commitCreativeBase64]);

    useEffect(() => {
        return () => {
            revokeCreativeEditorUrls(creativeEditor);
        };
    }, [creativeEditor, revokeCreativeEditorUrls]);

    return {
        campaignHorizontalContent,
        campaignVerticalContent,
        creativeEditor,
        cropExporting,
        creativeFileNames,
        creativeProcessing,
        processCreativeUpload,
        handleCreativeEditorCancel,
        handleCropNext,
        handlePreviewNext,
        resetCreatives,
    };
}

export function formatDateInputValue(date) {
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) {
        return '';
    }
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

export function defaultCampaignStartDate() {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 1);
}

export function defaultCampaignEndDate(startDate) {
    const start = startDate instanceof Date ? startDate : new Date(startDate);
    return new Date(start.getFullYear(), start.getMonth() + 1, 1);
}
